import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, GraduationCap } from 'lucide-react';
import toast from 'react-hot-toast';
import ProctorCamera from '../components/ProctorCamera';
import GamifiedQuestionRenderer from '../features/edutech/gamified';
import useEnterpriseProctoring from '../hooks/useEnterpriseProctoring';
import ProctoringAlertBanner from '../components/assessment/ProctoringAlertBanner';
import ProctoringStatusPanel from '../components/assessment/ProctoringStatusPanel';
import {
  startEdutechAssessment,
  getEdutechQuestions,
  submitEdutechAssessment
} from '../api/edutechApi';
import { banks as gamifiedBanks } from '../features/edutech/gamified/questionBank.js';

const SECTION_BLUEPRINT = [
  {
    key: 'pedagogical_foundations',
    name: 'Pedagogical Foundations',
    description:
      'Covers foundational theories of learning, instructional design, educational psychology, and teaching models.'
  },
  {
    key: 'classroom_situation_handling',
    name: 'Classroom & Situation Handling',
    description:
      'Scenario-based questions on classroom conflict, student behavior management, parent communication, and inclusive practices.'
  },
  {
    key: 'digital_literacy_edtech',
    name: 'Digital Literacy & EdTech Tools',
    description:
      'Evaluates knowledge of LMS platforms, AI tools in education, hybrid teaching, digital citizenship, and technology integration.'
  },
  {
    key: 'subject_academic_knowledge',
    name: 'Subject & Academic Knowledge',
    description:
      'Tests CBSE curriculum knowledge, question paper design, academic assessment rubrics, subject pedagogy, and academic integrity.'
  },
  {
    key: 'ethics_policy_leadership',
    name: 'Professional Ethics, Policy & Leadership',
    description:
      'Covers NEP 2020, Right to Education Act, teacher professional ethics, school leadership frameworks, and child protection policies.'
  }
];

const OCEAN_BLUEPRINT = [
  'Openness',
  'Conscientiousness',
  'Extraversion',
  'Agreeableness',
  'Neuroticism'
];

const mapGamifiedQuestion = (question, trait = null) => ({
  id: question.id,
  trait,
  text: question.text,
  narration: question.scene?.elements?.narration || question.narration || '',
  sceneType: question.scene?.type || 'default',
  gameType: question.gameType,
  scene: question.scene,
  difficulty: question.difficulty || 'medium',
  options: (question.options || []).map((option) => ({
    label: option.label,
    text: option.text
  }))
});

const buildGamifiedSections = () => {
  const coreSections = [
    {
      key: 'pedagogical_foundations',
      name: 'Pedagogical Foundations',
      sectionType: 'core',
      description:
        'Scene-driven pedagogy decisions on Bloom alignment, differentiation, assessment design, and instructional strategy.',
      questions: (gamifiedBanks.pedagogical || []).map((question) => mapGamifiedQuestion(question))
    },
    {
      key: 'classroom_situation_handling',
      name: 'Classroom & Situation Handling',
      sectionType: 'core',
      description:
        'Interactive classroom simulation scenarios covering behavior management, conflict response, and prioritization under pressure.',
      questions: (gamifiedBanks.classroom || []).map((question) => mapGamifiedQuestion(question))
    },
    {
      key: 'digital_literacy_edtech',
      name: 'Digital Literacy & EdTech Tools',
      sectionType: 'core',
      description:
        'Monitor-based classroom tech incidents including LMS failures, privacy, accessibility, and tool-selection decisions.',
      questions: (gamifiedBanks.digital || []).map((question) => mapGamifiedQuestion(question))
    },
    {
      key: 'subject_academic_knowledge',
      name: 'Subject & Academic Knowledge',
      sectionType: 'core',
      description:
        'Student misconception scenarios focused on conceptual diagnosis, correction quality, and academic response depth.',
      questions: (gamifiedBanks.subject || []).map((question) => mapGamifiedQuestion(question))
    },
    {
      key: 'ethics_policy_leadership',
      name: 'Professional Ethics, Policy & Leadership',
      sectionType: 'core',
      description:
        'Office-based ethical dilemma scenes covering policy integrity, confidentiality, fairness, and leadership judgement.',
      questions: (gamifiedBanks.ethics || []).map((question) => mapGamifiedQuestion(question))
    }
  ];

  const oceanSections = [
    {
      key: 'ocean_openness',
      name: 'OCEAN - Openness',
      sectionType: 'ocean',
      description:
        'Curiosity and innovation response scenes measuring exploration vs routine preference in teaching practice.',
      questions: (gamifiedBanks.openness || []).map((question) => mapGamifiedQuestion(question, 'Openness'))
    },
    {
      key: 'ocean_conscientiousness',
      name: 'OCEAN - Conscientiousness',
      sectionType: 'ocean',
      description:
        'Priority inbox interactions evaluating planning discipline, execution reliability, and structured task handling.',
      questions: (gamifiedBanks.conscientiousness || []).map((question) =>
        mapGamifiedQuestion(question, 'Conscientiousness')
      )
    },
    {
      key: 'ocean_extraversion',
      name: 'OCEAN - Extraversion',
      sectionType: 'ocean',
      description:
        'Staff-room social positioning scenes measuring interaction energy, initiative, and collaborative presence.',
      questions: (gamifiedBanks.extraversion || []).map((question) => mapGamifiedQuestion(question, 'Extraversion'))
    },
    {
      key: 'ocean_agreeableness',
      name: 'OCEAN - Agreeableness',
      sectionType: 'ocean',
      description:
        'People-vs-rules moral dilemmas measuring cooperative orientation, empathy, and conflict balancing.',
      questions: (gamifiedBanks.agreeableness || []).map((question) =>
        mapGamifiedQuestion(question, 'Agreeableness')
      )
    },
    {
      key: 'ocean_neuroticism',
      name: 'OCEAN - Neuroticism',
      sectionType: 'ocean',
      description:
        'Stress-load simulations with notification storms measuring emotional regulation under instructional pressure.',
      questions: (gamifiedBanks.neuroticism || []).map((question) => mapGamifiedQuestion(question, 'Neuroticism'))
    }
  ];

  return [...coreSections, ...oceanSections];
};

const EduTechAssessmentPage = () => {
  const navigate = useNavigate();
  const [cameraActive, setCameraActive] = useState(false);
  // MODIFIED: photo capture moved to SharedVerifyPage at verify route.
  const [profilePhoto] = useState(() => sessionStorage.getItem('edutech_verify_photo'));
  const [showSectionBanner, setShowSectionBanner] = useState(false);
  const [bannerSectionName, setBannerSectionName] = useState('');
  const [isBannerExiting, setIsBannerExiting] = useState(false);
  const hasStartedRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const videoRef = useRef(null);
  const testStreamRef = useRef(null);
  const forceSubmitRef = useRef(() => {});
  const [lastAnswerTimestamp, setLastAnswerTimestamp] = useState(Date.now());
  const [state, setState] = useState({
    step: 1,
    attemptId: null,
    questions: [],
    currentSectionIndex: 0,
    currentQuestionIndex: 0,
    answers: {},
    proctoringData: null,
    isSubmitting: false,
    error: null
  });

  const {
    proctoringReport,
    currentAlert,
    dismissAlert,
    violations,
    integrityScore,
    faceStatus,
    gazeStatus,
    isFullscreen,
    requestFullscreen,
    recordAnswer
  } = useEnterpriseProctoring({
    assessmentKey: 'edutech',
    videoRef,
    enabled: true,
    onViolation: (type, detail) => {
      console.warn('[Proctoring]', type, detail);
    },
    onForcedSubmit: () => forceSubmitRef.current?.(),
    maxViolations: 10
  });

  const sections = state.questions;
  const currentSection = sections[state.currentSectionIndex] || null;
  const currentSectionQuestions = currentSection?.questions || [];
  const currentQuestion = currentSectionQuestions[state.currentQuestionIndex] || null;
  const globalQuestionIndex =
    sections
      .slice(0, state.currentSectionIndex)
      .reduce((sum, section) => sum + (section.questions?.length || 0), 0) +
    state.currentQuestionIndex +
    1;
  const currentTraitName =
    currentSection?.sectionType === 'ocean'
      ? String(currentSection?.name || '')
          .replace(/^OCEAN\s*-\s*/i, '')
          .trim()
      : null;
  const isLastQuestion =
    state.currentSectionIndex === sections.length - 1 &&
    (currentSectionQuestions.length === 0 ||
      state.currentQuestionIndex === currentSectionQuestions.length - 1);

  const totalQuestions = useMemo(
    () => sections.reduce((sum, section) => sum + (section.questions?.length || 0), 0),
    [sections]
  );

  const answeredCount = Object.keys(state.answers).length;

  useEffect(() => {
    // MODIFIED: enforce verify route before test entry.
    if (!profilePhoto) {
      navigate('/assessment/edutech/verify');
    }
  }, [navigate, profilePhoto]);

  useEffect(() => {
    // ADDED: hidden webcam stream for enterprise proctoring.
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: false })
      .then((stream) => {
        testStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(() => {
        // Proctoring degrades gracefully if camera stream cannot be started here.
      });

    return () => {
      if (testStreamRef.current) {
        testStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleCameraLost = useCallback((reason) => {
    setCameraActive(false);
    setState((prev) => ({
      ...prev,
      step: 1,
      error: 'Camera connection was lost. Please restore camera access to continue.'
    }));
  }, []);

  const handleCameraStatus = useCallback((active) => {
    setCameraActive(active);
    if (active) {
      setState((prev) => ({
        ...prev,
        proctoringData: {
          ...(prev.proctoringData || {}),
          cameraVerifiedAt: new Date().toISOString()
        }
      }));
    }
  }, []);

  const beginInstructions = () => {
    if (!cameraActive) {
      toast.error('Camera access is required to continue');
      return;
    }
    setState((prev) => ({ ...prev, step: 2, error: null }));
  };

  const startAssessment = async () => {
    if (hasStartedRef.current) {
      return;
    }

    if (!profilePhoto) {
      toast.error('Profile photo is required before starting Edutech assessment');
      // MODIFIED: redirect to verify route when photo is missing.
      navigate('/assessment/edutech/verify');
      return;
    }

    if (!cameraActive) {
      toast.error('Camera access is required to continue');
      return;
    }

    try {
      hasStartedRef.current = true;
      setState((prev) => ({ ...prev, isSubmitting: true, error: null }));
      const startResponse = await startEdutechAssessment();
      // Keep the existing API call alive for compatibility, but source UX questions from
      // the new local gamified bank for this phase.
      try {
        await getEdutechQuestions(startResponse.attemptId);
      } catch (_ignorePoolError) {
        // Intentionally ignored in gamified-bank phase.
      }
      const loadedSections = buildGamifiedSections();

      setState((prev) => ({
        ...prev,
        attemptId: startResponse.attemptId,
        questions: loadedSections,
        currentSectionIndex: 0,
        currentQuestionIndex: 0,
        answers: {},
        isSubmitting: false,
        error: null
      }));

      showBannerThenProceed(loadedSections[0]?.name || SECTION_BLUEPRINT[0]?.name || 'Section 1', () => {
        setState((prev) => ({ ...prev, step: 3 }));
      });
    } catch (error) {
      hasStartedRef.current = false;
      setState((prev) => ({
        ...prev,
        isSubmitting: false,
        error: error.response?.data?.error || 'Unable to start Edutech assessment'
      }));
    }
  };

  const handleSelectAnswer = (selectedKey) => {
    if (!currentQuestion) return;
    setState((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [currentQuestion.id]: selectedKey
      }
    }));
    // ADDED: enterprise proctoring rapid-answer capture.
    recordAnswer(Date.now() - lastAnswerTimestamp);
    setLastAnswerTimestamp(Date.now());
  };

  const showBannerThenProceed = useCallback((sectionName, afterBanner) => {
    setBannerSectionName(sectionName);
    setShowSectionBanner(true);
    setIsBannerExiting(false);
    setTimeout(() => {
      setIsBannerExiting(true);
      setTimeout(() => {
        setShowSectionBanner(false);
        setIsBannerExiting(false);
        if (typeof afterBanner === 'function') {
          afterBanner();
        }
      }, 300);
    }, 1700);
  }, []);

  const handleNext = () => {
    if (!currentSection) return;

    if (currentSectionQuestions.length === 0) {
      if (state.currentSectionIndex < sections.length - 1) {
        const nextSectionIndex = state.currentSectionIndex + 1;
        showBannerThenProceed(sections[nextSectionIndex]?.name || `Section ${nextSectionIndex + 1}`, () => {
          setState((prev) => ({
            ...prev,
            currentSectionIndex: nextSectionIndex,
            currentQuestionIndex: 0
          }));
        });
        return;
      }
      submitAssessment();
      return;
    }

    if (state.currentQuestionIndex < currentSectionQuestions.length - 1) {
      setState((prev) => ({ ...prev, currentQuestionIndex: prev.currentQuestionIndex + 1 }));
      return;
    }

    if (state.currentSectionIndex < sections.length - 1) {
      const nextSectionIndex = state.currentSectionIndex + 1;
      showBannerThenProceed(sections[nextSectionIndex]?.name || `Section ${nextSectionIndex + 1}`, () => {
        setState((prev) => ({
          ...prev,
          currentSectionIndex: nextSectionIndex,
          currentQuestionIndex: 0
        }));
      });
      return;
    }

    submitAssessment();
  };

  const handlePrevious = () => {
    if (state.currentQuestionIndex <= 0) return;
    setState((prev) => ({ ...prev, currentQuestionIndex: prev.currentQuestionIndex - 1 }));
  };

  const submitAssessment = async () => {
    if (submitInFlightRef.current || state.isSubmitting) {
      return;
    }

    if (!state.attemptId) {
      console.error('[Edutech] Cannot submit: attemptId is not set');
      setState((prev) => ({
        ...prev,
        error: 'Session expired. Please refresh and start again.'
      }));
      return;
    }

    try {
      submitInFlightRef.current = true;
      setState((prev) => ({ ...prev, isSubmitting: true, error: null }));

      const answersPayload = Object.entries(state.answers).map(([questionId, selectedKey]) => ({
        questionId,
        selectedKey
      }));

      const result = await submitEdutechAssessment(
        state.attemptId,
        answersPayload,
        // MODIFIED: proctoringData now from useEnterpriseProctoring hook.
        proctoringReport,
        profilePhoto
      );
      // Clear assignment invite ref after successful final submit.
      sessionStorage.removeItem('assessment_ref');
      toast.success('Edutech assessment submitted successfully');
      navigate('/thank-you', {
        state: {
          attemptId: result.attemptId || state.attemptId,
          assessmentType: 'edutech',
          result
        }
      });
    } catch (error) {
      submitInFlightRef.current = false;
      setState((prev) => ({
        ...prev,
        isSubmitting: false,
        error: error.response?.data?.error || 'Failed to submit Edutech assessment'
      }));
    }
  };
  forceSubmitRef.current = () => {
    if (!state.isSubmitting) {
      submitAssessment();
    }
  };

  return (
    <div className="assessment-shell min-h-screen">
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        aria-hidden="true"
      />
      <ProctoringAlertBanner message={currentAlert} onDismiss={dismissAlert} />
      <ProctoringStatusPanel
        faceStatus={faceStatus}
        gazeStatus={gazeStatus}
        violations={violations}
        integrityScore={integrityScore}
        isFullscreen={isFullscreen}
        onRequestFullscreen={requestFullscreen}
        videoRef={videoRef}
      />
      <div className="floating-orb orb-a" />
      <div className="floating-orb orb-b" />
      <div className="floating-orb orb-c" />
      {showSectionBanner && (
        <div className={`edutech-section-banner ${isBannerExiting ? 'exiting' : ''}`}>
          <span className="edutech-section-banner__label">Section</span>
          <h2 className="edutech-section-banner__name">{bannerSectionName}</h2>
        </div>
      )}

      {/* MODIFIED: proctoring overlays always render since verify step is now route-based. */}
      <>
        <ProctorCamera onCameraStatus={handleCameraStatus} onCameraLost={handleCameraLost} />
      </>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {state.step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-elevated rounded-2xl p-8 text-center"
          >
            <GraduationCap className="w-14 h-14 mx-auto text-indigo-600 mb-4" />
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Edutech Assessment</h1>
            <p className="text-slate-700 mb-6">
              {/* MODIFIED: profile capture happens at /assessment/edutech/verify. */}
              Step 1 of 4: Verify camera status and initialize proctoring setup.
            </p>
            <p className="text-sm text-slate-600 mb-8">
              Camera status: <span className={cameraActive ? 'text-green-600' : 'text-red-600'}>
                {cameraActive ? 'Active' : 'Not active'}
              </span>
            </p>
            <p className="text-sm text-slate-600 mb-8">
              Profile photo:{' '}
              <span className={profilePhoto ? 'text-green-600' : 'text-red-600'}>
                {profilePhoto ? 'Captured' : 'Not captured'}
              </span>
            </p>
            <button
              type="button"
              onClick={beginInstructions}
              disabled={!profilePhoto}
              className="neu-cta inline-flex items-center justify-center rounded-xl text-sm font-semibold h-11 px-8 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </motion.div>
        )}

        {state.step === 2 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-elevated rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Edutech Assessment</h2>
            <p className="text-slate-700 mb-2">5 Core Sections + 5 OCEAN Trait Sections (10 questions each)</p>
            <p className="text-sm text-slate-600 mb-6">
              This phase runs only the newly designed gamified bank: 10 sections x 10 questions = 100 total.
            </p>
            <ul className="space-y-4 mb-8">
              {SECTION_BLUEPRINT.map((section) => (
                <li key={section.key} className="neu-surface rounded-xl p-4">
                  <h3 className="font-semibold text-slate-900">{section.name}</h3>
                  <p className="text-sm text-slate-600 mt-1">{section.description}</p>
                </li>
              ))}
              <li className="neu-surface rounded-xl p-4">
                <h3 className="font-semibold text-slate-900">OCEAN Personality Profiling</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Traits: {OCEAN_BLUEPRINT.join(', ')} - 10 gamified scene questions per trait.
                </p>
              </li>
            </ul>
            {state.error && <p className="text-sm text-red-600 mb-4">{state.error}</p>}
            <button
              type="button"
              onClick={startAssessment}
              disabled={state.isSubmitting || !cameraActive || !profilePhoto}
              className="neu-cta inline-flex items-center justify-center rounded-xl text-sm font-semibold h-11 px-8 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {state.isSubmitting ? 'Loading Questions...' : 'Begin Assessment'}
            </button>
          </motion.div>
        )}

        {state.step === 3 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-elevated rounded-2xl p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">
                Section {state.currentSectionIndex + 1} of {Math.max(sections.length, 1)}
              </h2>
              <p className="text-sm text-slate-600">
                {totalQuestions > 0 ? `${answeredCount}/${totalQuestions} answered` : 'No questions loaded yet'}
              </p>
            </div>

            {currentSection ? (
              <>
                <h3 className="text-lg font-semibold text-indigo-700 mb-2">{currentSection.name}</h3>
                <p className="text-sm text-slate-600 mb-6">{currentSection.description}</p>

                {currentSectionQuestions.length > 0 && currentQuestion ? (
                  <>
                    <div className="mb-6">
                      <GamifiedQuestionRenderer
                        question={{
                          id: currentQuestion.id,
                          text: currentQuestion.text,
                          narration: currentQuestion.narration,
                          options: (currentQuestion.options || []).map((option, index) => ({
                            label: option.label || option.key || String.fromCharCode(65 + index),
                            text: option.text || option.label || option.key
                          })),
                          gameType: currentQuestion.gameType,
                          scene: currentQuestion.scene,
                          section: currentSection?.key,
                          trait: currentQuestion?.trait || currentTraitName,
                          type: currentSection?.sectionType === 'ocean' ? 'ocean' : 'core'
                        }}
                        questionIndex={globalQuestionIndex}
                        totalQuestions={totalQuestions}
                        onAnswer={(questionId, selectedOptionLabel) => {
                          void questionId;
                          handleSelectAnswer(selectedOptionLabel);
                        }}
                        sectionName={currentSection?.name || 'Section'}
                        traitName={currentTraitName}
                        sectionQuestionIndex={state.currentQuestionIndex}
                        sectionQuestionCount={currentSectionQuestions.length}
                        selectedOption={state.answers[currentQuestion.id]}
                      />
                    </div>
                  </>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 mb-6">
                    No questions available for this section in Phase 1. Phase 2 will populate the question bank.
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    disabled={state.currentQuestionIndex === 0 || currentSectionQuestions.length === 0}
                    className="inline-flex items-center rounded-xl text-sm font-medium h-10 px-4 border border-slate-300 bg-white/80 text-slate-700 disabled:opacity-50"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={isLastQuestion ? submitAssessment : handleNext}
                    disabled={
                      state.isSubmitting ||
                      (currentSectionQuestions.length > 0 &&
                        currentQuestion &&
                        !state.answers[currentQuestion.id])
                    }
                    className="neu-cta inline-flex items-center rounded-xl text-sm font-semibold h-10 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {state.isSubmitting ? 'Submitting...' : isLastQuestion ? 'Submit' : 'Next'}
                    {!isLastQuestion && <ArrowRight className="w-4 h-4 ml-2" />}
                  </button>
                </div>
                {state.error && <p className="text-sm text-red-600 mt-4">{state.error}</p>}
              </>
            ) : (
              <div className="text-sm text-gray-600">No sections returned for this attempt.</div>
            )}
          </motion.div>
        )}
      </div>
      <style>{`
        .edutech-section-banner {
          position: fixed;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          z-index: 50;
          animation: edutechBannerIn 0.35s ease forwards;
        }

        .edutech-section-banner.exiting {
          animation: edutechBannerOut 0.3s ease forwards;
        }

        .edutech-section-banner__label {
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 0.5rem;
        }

        .edutech-section-banner__name {
          font-size: clamp(1.5rem, 4vw, 2.5rem);
          font-weight: 700;
          color: #ffffff;
          text-align: center;
          padding: 0 1.5rem;
        }

        @keyframes edutechBannerIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }

        @keyframes edutechBannerOut {
          from { opacity: 1; transform: scale(1); }
          to { opacity: 0; transform: scale(1.02); }
        }
      `}</style>
    </div>
  );
};

export default EduTechAssessmentPage;
